import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import Hero from '../components/Hero';
import Section from '../components/Section';
import Button from '../components/Button';
import { FaUsers, FaUserTie, FaBuilding, FaLandmark, FaHotel, FaBrain, FaGavel, FaHome, FaUtensils, FaTooth, FaHeart, FaHandHoldingMedical, FaPencilRuler, FaRocket, FaCut, FaCoffee, FaCar, FaPaintRoller, FaPrescriptionBottleAlt, FaFileInvoice, FaPizzaSlice, FaMusic } from 'react-icons/fa';
import { MdLocationCity } from 'react-icons/md';

// Sección personalizada para un diseño más compacto
const CustomSection = styled(Section)`
  padding-top: 3rem !important;
  padding-bottom: 3rem !important;
  
  & > div > div {
    margin-bottom: 1.5rem;
  }
  
  h2 {
    font-size: 1.75rem;
    margin-bottom: 0.5rem;
  }
  
  p {
    margin-bottom: 0.75rem;
    font-size: 0.95rem;
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  margin: 2rem 0;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled(Link)`
  display: block;
  background-color: var(--white);
  border-radius: 1rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: all 0.3s ease;
  text-decoration: none;
  color: inherit;
  height: 100%;
  position: relative;
  padding: 2rem;
  padding-top: 4rem;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
    text-decoration: none;
  }
`;

const ServiceIcon = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  color: var(--white);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.8rem;
  border-radius: 0 0 1rem 0;
  font-size: 1.2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const ServiceTitle = styled.h3`
  font-size: 1.4rem;
  margin-bottom: 1rem;
  font-weight: 600;
  color: var(--gray-dark);
`;

const ServiceDescription = styled.p`
  font-size: 1rem;
  color: var(--gray);
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const ServicePrice = styled.div`
  font-weight: 700;
  color: var(--blue);
  font-size: 1.1rem;
  margin-top: auto;
  padding-top: 1rem;
  border-top: 1px dashed #eee;
`;

const CategoryTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  margin: 3rem 0 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 3px solid #f0f0f0;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -3px;
    left: 0;
    width: 100px;
    height: 3px;
    background: linear-gradient(90deg, #007bff 0%, #0056b3 100%);
  }
`;

const CTAContainer = styled.div`
  text-align: center;
  margin: 4rem 0 1rem;
  padding: 2.5rem;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e9f2 100%);
  border-radius: 1rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 6px;
    background: linear-gradient(90deg, #007bff 0%, #0056b3 100%);
  }
  
  h2 {
    font-size: 1.8rem;
    margin-bottom: 1rem;
    font-weight: 700;
  }
  
  p {
    font-size: 1.1rem;
    margin-bottom: 1.5rem;
    color: var(--gray);
  }
`;

const CompactHero = styled(Hero)`
  & > div {
    padding-top: 3rem !important;
    padding-bottom: 3rem !important;
  }
  
  h1 {
    font-size: 2.25rem !important;
  }
  
  p {
    font-size: 1rem !important;
  }
`;

const ServiciosPage = () => {
  // Servicios por tipo de cliente
  const clientTypeServices = [
    {
      title: 'Páginas Web para Autónomos',
      description: 'Diseño web para freelancers y profesionales independientes que buscan aumentar su visibilidad y conseguir más clientes.',
      icon: <FaUserTie />,
      price: 'Desde 200€',
      link: '/servicios/diseno-web-autonomos'
    },
    {
      title: 'Páginas Web para Pymes',
      description: 'Soluciones web profesionales para pequeñas y medianas empresas que necesitan mejorar su presencia online.',
      icon: <FaUsers />,
      price: 'Desde 200€',
      link: '/servicios/diseno-web-pymes'
    },
    {
      title: 'Webs Corporativas',
      description: 'Páginas web corporativas para empresas que necesitan transmitir profesionalidad y generar confianza.',
      icon: <FaBuilding />,
      price: 'Desde 200€',
      link: '/servicios/diseno-web-empresas'
    }
  ];

  // Otros tipos de webs
  const otherWebTypes = [
    {
      title: 'Landing Pages',
      description: 'Páginas de aterrizaje optimizadas para campañas publicitarias y conversión de visitantes en clientes.',
      icon: <FaRocket />,
      price: 'Desde 150€',
      link: '/servicios/landing-pages'
    },
    {
      title: 'Web para Bodas',
      description: 'Invitaciones digitales con confirmación automática y galerías de fotos para tu boda.',
      icon: <FaHeart />,
      price: 'Desde 99€',
      link: '/servicios/diseno-web-bodas'
    }
  ];

  // Servicios por sector
  const sectorServices = [
    {
      title: 'Webs para Abogados',
      description: 'Páginas web para despachos jurídicos y abogados independientes, diseñadas para transmitir profesionalidad.',
      icon: <FaGavel />,
      price: 'Desde 200€',
      link: '/servicios/diseno-web-abogados'
    },
    {
      title: 'Webs para Restaurantes',
      description: 'Diseño web para bares, cafeterías y restaurantes que necesitan mostrar su carta y sistema de reservas.',
      icon: <FaUtensils />,
      price: 'Desde 200€',
      link: '/servicios/diseno-web-restaurantes'
    },
    {
      title: 'Webs para Inmobiliarias',
      description: 'Soluciones web para agentes inmobiliarios, con sistemas para mostrar propiedades.',
      icon: <FaHome />,
      price: 'Desde 200€',
      link: '/servicios/diseno-web-inmobiliarias'
    },
    {
      title: 'Webs para Dentistas',
      description: 'Diseño web para clínicas dentales, con secciones para servicios, equipo médico y sistema de citas.',
      icon: <FaTooth />,
      price: 'Desde 200€',
      link: '/servicios/diseno-web-dentistas'
    },
    {
      title: 'Webs para Psicólogos',
      description: 'Diseño web para psicólogos con sistema de citas y optimización local.',
      icon: <FaBrain />,
      price: 'Desde 200€',
      link: '/servicios/diseno-web-psicologos'
    },
    {
      title: 'Webs para Hoteles',
      description: 'Diseño web para hoteles con sistema de reservas sin comisiones.',
      icon: <FaHotel />,
      price: 'Desde 200€',
      link: '/servicios/diseno-web-hoteles'
    },
    {
      title: 'Web para Terapeutas',
      description: 'Páginas web para terapeutas con sistema de citas y blog especializado.',
      icon: <FaHandHoldingMedical />,
      price: 'Desde 200€',
      link: '/servicios/diseno-web-terapeutas'
    },
    {
      title: 'Web para Arquitectos',
      description: 'Portfolio digital profesional para arquitectos y estudios de arquitectura.',
      icon: <FaPencilRuler />,
      price: 'Desde 200€',
      link: '/servicios/diseno-web-arquitectos'
    },
    {
      title: 'Web para Constructoras',
      description: 'Portfolio visual para empresas constructoras con presentación de obras y proyectos.',
      icon: <FaBuilding />,
      price: 'Desde 200€',
      link: '/servicios/diseno-web-constructoras'
    },
    {
      title: 'Web para Peluquerías',
      description: 'Diseño web para peluquerías y salones de belleza con galería de trabajos realizados.',
      icon: <FaCut />,
      price: 'Desde 200€',
      link: '/servicios/diseno-web-peluqueria'
    },
    {
      title: 'Web para Cafeterías',
      description: 'Páginas web para cafeterías y coffee shops con carta digital y presentación de ambiente.',
      icon: <FaCoffee />,
      price: 'Desde 200€',
      link: '/servicios/diseno-web-cafeteria'
    },
    {
      title: 'Web para Talleres Mecánicos',
      description: 'Páginas web para talleres mecánicos con presentación de servicios y sistema de citas.',
      icon: <FaCar />,
      price: 'Desde 200€',
      link: '/servicios/diseno-web-taller'
    },
    {
      title: 'Web para Pintores',
      description: 'Portfolio digital para pintores con galería de trabajos antes/después y optimización local.',
      icon: <FaPaintRoller />,
      price: 'Desde 200€',
      link: '/servicios/diseno-web-pintor'
    },
    {
      title: 'Web para Farmacias',
      description: 'Diseño web para farmacias con información de guardias, servicios especiales y localización.',
      icon: <FaPrescriptionBottleAlt />,
      price: 'Desde 200€',
      link: '/servicios/diseno-web-farmacia'
    },
    {
      title: 'Web para Gestorías',
      description: 'Páginas web para gestorías y asesorías con presentación clara de servicios y área de clientes.',
      icon: <FaFileInvoice />,
      price: 'Desde 200€',
      link: '/servicios/diseno-web-gestoria'
    },
    {
      title: 'Web para Pizzerías',
      description: 'Diseño web específico para pizzerías con carta digital, sistema de pedidos y optimización local.',
      icon: <FaPizzaSlice />,
      price: 'Desde 200€',
      link: '/servicios/diseno-web-pizzeria'
    },
    {
      title: 'Web para Discotecas',
      description: 'Páginas web para discotecas y locales de ocio nocturno con calendario de eventos y galería multimedia.',
      icon: <FaMusic />,
      price: 'Desde 200€',
      link: '/servicios/diseno-web-discoteca'
    }
  ];

  return (
    <Layout>
      <SEO 
        title="Servicios de Diseño Web a Bajo Coste"
        description="Servicios de diseño y desarrollo web para todo tipo de negocios. Páginas web baratas para autónomos, pymes y empresas, optimizadas para SEO."
        keywords="servicios diseño web, desarrollo web, páginas web baratas, webs económicas"
        canonical="https://webabajocoste.es/servicios"
      />
      
      <CompactHero 
        title="Nuestros Servicios de Diseño Web"
        subtitle="Soluciones web a medida para diferentes tipos de negocios"
        backgroundImage="/fotos generales para el proyecto/servicios-hero.webp"
      />
      
      <CustomSection 
        title="Hacemos webs baratas para negocios de cualquier tamaño"
        subtitle="Soluciones adaptadas a las necesidades específicas de cada tipo de negocio"
      >
        <ServicesGrid>
          {clientTypeServices.map((service, index) => (
            <ServiceCard key={index} to={service.link}>
              <ServiceIcon>
                {service.icon}
              </ServiceIcon>
              <ServiceTitle>{service.title}</ServiceTitle>
              <ServiceDescription>{service.description}</ServiceDescription>
              <ServicePrice>{service.price}</ServicePrice>
            </ServiceCard>
          ))}
        </ServicesGrid>
        
        <CategoryTitle>Otros tipos de webs</CategoryTitle>
        
        <ServicesGrid>
          {otherWebTypes.map((service, index) => (
            <ServiceCard key={index} to={service.link}>
              <ServiceIcon>
                {service.icon}
              </ServiceIcon>
              <ServiceTitle>{service.title}</ServiceTitle>
              <ServiceDescription>{service.description}</ServiceDescription>
              <ServicePrice>{service.price}</ServicePrice>
            </ServiceCard>
          ))}
        </ServicesGrid>
        
        <CategoryTitle>Servicios por Sector</CategoryTitle>
        
        <ServicesGrid>
          {sectorServices.map((service, index) => (
            <ServiceCard key={index} to={service.link}>
              <ServiceIcon>
                {service.icon}
              </ServiceIcon>
              <ServiceTitle>{service.title}</ServiceTitle>
              <ServiceDescription>{service.description}</ServiceDescription>
              <ServicePrice>{service.price}</ServicePrice>
            </ServiceCard>
          ))}
        </ServicesGrid>
        
        <CTAContainer>
          <h2>¿No encuentras lo que buscas?</h2>
          <p>Podemos adaptar nuestros servicios a tus necesidades específicas</p>
          <Button 
            as="a" 
            href="/contacto" 
            size="medium"
            variant="primary"
            style={{ margin: '0.5rem 0' }}
          >
            Solicitar presupuesto personalizado
          </Button>
        </CTAContainer>
      </CustomSection>
    </Layout>
  );
};

export default ServiciosPage; 